import { LayoutServicePageState, RichText, useSitecoreContext, TextField } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';

import withStyles from 'lib/enhancers/withStyles';
import { dataFetcher } from 'lib/data-fetcher';
import { getQueryStringParameter, getCleanID } from 'lib/utils';

import type { TableCellData, TableRowData } from 'components/helpers/Table/Table.types';
import Table, { TableProps } from 'components/helpers/Table/Table';
import Loader, { LoaderVariant } from 'components/helpers/Loader/Loader';
import Link from 'components/helpers/Link/Link';
import Modal from 'components/helpers/Modal';
import Tooltip from 'components/helpers/Tooltip/Tooltip';
import { buildURL, buildApplyToEnrollURL, checkEnrollEnable } from 'components/PlanData/utilities';

import styles from './PlanComparisonTable.module.scss';
import { PlanType } from '../Types/PlanTypes.Type';
import { useDictionary } from 'lib/utils';
import { DrugCoverageTableGroup } from '../Types/DrugCoverageTables.Type';
import { PlanYear } from '../Types/PlanYear.Type';
import { PlanDetailGroup } from '../Types/PlanDetailGroup.Type';
import { ComponentProps } from 'lib/component-props';
import { PlanResponse } from '../Types/GetPlanDetailsResponse.Type';

const cx = classNames.bind(styles);

enum LoadingState {
  start = 'Starting',
  loadingPlans = 'Loading plans',
  plansLoaded = 'Plans loaded',
  loadTopRows = 'Loading top rows',
  topRowsLoaded = 'Top rows loaded',
  loadTables = 'Loading tables',
  end = 'Finished',
}

type PlanComparisonTableFields = {
  planType: PlanType;
  planDetailGroupLayout: Array<PlanDetailGroup>;
  rowNotCoveredText: TextField;
  rxTableNotCoveredText: TextField;
  rxRowNotCoveredText: TextField;
  showRxDrugCoverageTable: {
    value: boolean;
  };
  drugCoverageGroups: Array<DrugCoverageTableGroup>;
  planYears: PlanYear[];
};

type PlanComparisonTableProps = ComponentProps & {
  fields: PlanComparisonTableFields;
};

const PlanComparisonTable = (props: PlanComparisonTableProps): JSX.Element => {
  const router = useRouter();
  const { sitecoreContext } = useSitecoreContext();
  const viewPlanDetailsLabel = useDictionary('View Plan Details');
  const viewApplyToEnrollLabel = useDictionary('Apply to Enroll');

  const {
    planType,
    planDetailGroupLayout,
    drugCoverageGroups,
    showRxDrugCoverageTable,
    rxTableNotCoveredText,
    rxRowNotCoveredText,
    rowNotCoveredText,
    planYears,
  } = props.fields;
  const { planSearchPage, planDetailPage } = planType.fields;

  const [stage, setStage] = useState<LoadingState>(LoadingState.start);
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [tables, setTables] = useState<TableProps[]>([]);
  const [topRow, setTopRow] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [topRowNoLink, setTopRowNoLink] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [applyRow, setApplyRow] = useState<TableCellData[]>([{ children: '', className: cx('placeholder') }]);
  const [showNoResultsFound, setShowNoResultsFound] = useState<boolean>(false);
  const baseUrl = process.env['PUBLIC_URL'] || '';
  const county = getQueryStringParameter('county', router.query);
  const year = getQueryStringParameter('year', router.query);
  const zipcode = getQueryStringParameter('zipcode', router.query);
  const existingMember = getQueryStringParameter('ExistingMember', router.query);
  const [enableEnroll, setEnableEnroll] = useState<boolean>();

  useEffect(() => {
    function loadPlans() {
      setStage(LoadingState.loadingPlans);

      const sortPlansData = (plansList: PlanResponse[], plansOrder: string[]): PlanResponse[] => {
        if (!plansOrder?.length) {
          return plansList;
        }

        const sortedPlansList = plansList.slice().sort((a, b) => {
          const indexA = plansOrder?.indexOf(a.id);
          const indexB = plansOrder?.indexOf(b.id);

          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        });

        return sortedPlansList;
      };

      const plansInQueryString = Object.keys(router.query).filter((x) => x.startsWith('plan')).length;
      if (plansInQueryString === 0 || !county || !year || !zipcode || !existingMember) {
        router.push(new URL(planSearchPage.url ?? ''));
      } else {
        let usePlanID = getQueryStringParameter('plan0', router.query);
        let x = 0;

        const plansOrder: string[] = [];

        do {
          plansOrder.push(usePlanID);

          dataFetcher<PlanResponse[]>(`${baseUrl}/api/getPlanDetails`, {
            planTypeId: planType.id,
            planItemId: usePlanID,
            includeDocFields: false,
            includeBenefitsFields: false,
            language: sitecoreContext.language,
          })
            .then((response) => {
              const tempPlans = plans;
              tempPlans.push(response.data[0]);

              const $allPlansLoaded = plansInQueryString === tempPlans.length;

              if ($allPlansLoaded) {
                setPlans(sortPlansData(tempPlans, plansOrder));
              } else {
                setPlans(tempPlans);
              }

              setStage($allPlansLoaded ? LoadingState.plansLoaded : LoadingState.loadingPlans);
            })
            .catch(() => {
              setShowNoResultsFound(true);
            });
          x++;
          usePlanID = getQueryStringParameter('plan' + x, router.query);
        } while (usePlanID != '' || showNoResultsFound);
      }
    }

    function loadTopRows() {
      setStage(LoadingState.loadTopRows);

      plans.map((thisPlan) => {
        const tempQueries: [string, string][] = [
          ['planId', thisPlan.id],
          ['county', county],
          ['year', year],
          ['zipcode', zipcode],
          ['ExistingMember', existingMember],
        ];
        let tempRow = topRow;

        tempRow.push({
          children: (
            <>
              <RichText field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
              <a href={buildURL(planDetailPage.url || '', null, tempQueries, sitecoreContext.language)}>
                {viewPlanDetailsLabel}
              </a>
            </>
          ),
          className: cx('planName', 'col' + plans.length),
          isHeader: true,
        });

        setTopRow(tempRow);

        tempRow = topRowNoLink;
        tempRow.push({
          children: (
            <>
              <RichText field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
            </>
          ),
          className: cx('planName', 'col' + plans.length),
          isHeader: true,
        });
        setTopRowNoLink(tempRow);

        setEnableEnroll(checkEnrollEnable(planYears, parseInt(year)));

        const tempApplyRow = applyRow;
        tempApplyRow.push({
          children: (
            <>
              <RichText field={{ value: thisPlan.planName.value?.toString() }} editable={false} />
              <br />
              <Link
                field={{ value: {} }}
                href={buildApplyToEnrollURL(
                  planType.fields.name,
                  zipcode,
                  county,
                  year,
                  thisPlan.planName.value,
                  thisPlan.contract.value,
                  thisPlan.pbp.value,
                  thisPlan.groupId.value,
                  thisPlan.cavulusPlanName.value
                )}
                icon="carat"
              >
                {viewApplyToEnrollLabel}
              </Link>
            </>
          ),
          className: cx('planName', 'apply', 'col' + plans.length),
          isHeader: true,
        });
        setApplyRow(tempApplyRow);
      });
      setStage(LoadingState.topRowsLoaded);
    }

    if (sitecoreContext.pageState === LayoutServicePageState.Edit) {
      setStage(LoadingState.end);
      const newTable: TableProps = {
        id: 'sampleTable',
        rowData: [
          {
            placement: 'header',
            cellData: [
              { children: '', className: cx('placeholder') },
              { children: 'Plan 1', className: cx('planName', 'col' + plans.length) },
              { children: 'Plan 2', className: cx('planName', 'col' + plans.length) },
              { children: 'Plan 3', className: cx('planName', 'col' + plans.length) },
            ],
          },
          {
            placement: 'header',
            cellData: [
              {
                children: 'Table Name',
                colSpan: 4,
                className: cx('header'),
              },
            ],
          },
          {
            cellData: [
              { children: 'Row name', isHeader: true },
              { children: 'Plan 1 coverage' },
              { children: 'Plan 2 coverage' },
              { children: 'Plan 3 coverage' },
            ],
          },
          {
            cellData: [
              {
                children:
                  'Plan Comparison data is pulled from multiple sources in Global Data > Plan Data and must be changed there',
                colSpan: 4,
                className: cx('no-plan'),
              },
            ],
          },
        ],
        className: cx('plan-comparison-table'),
      };

      setTables([newTable]);
    }

    if (sitecoreContext.pageState === LayoutServicePageState.Normal) {
      if (router.isReady && stage === LoadingState.start) {
        loadPlans();
      }

      if (stage === LoadingState.plansLoaded) {
        loadTopRows();
      }

      if (stage === LoadingState.topRowsLoaded) {
        setStage(LoadingState.loadTables);
        const tempTables: TableProps[] = [];

        //Detail Tables
        planDetailGroupLayout.map((group) => {
          const newTable: TableProps = {
            id: group.id,
            rowData: [],
            className: cx('plan-comparison-table'),
          };

          //add top row, only first table should have links
          newTable.rowData?.push({
            placement: 'header',
            cellData: tempTables.length === 0 ? topRow : topRowNoLink,
          });

          //add table header with name(caption)
          const captionField = <RichText field={{ value: group.caption }} />;
          newTable.rowData?.push({
            placement: 'header',
            cellData: [
              {
                children: captionField,
                colSpan: plans.length + 1,
                className: cx('header'),
              },
            ],
          });

          let addTable = false;
          group.planDetailRows.map((thisRow) => {
            const cells: TableCellData[] = Array<TableCellData>();
            let labelField = <RichText field={{ value: thisRow.label }} />;
            if (thisRow.tooltip !== '') {
              labelField = (
                <div className={cx('tooltip-wrapper')}>
                  <RichText field={{ value: thisRow.label }} />
                  <Tooltip field={{ value: thisRow.tooltip }} className={cx('compare-tooltip')} direction="custom" />
                </div>
              );
            }

            cells.push({ children: labelField, isHeader: true });

            let showRow = false;

            plans.map((thisPlan) => {
              let cellField = <RichText field={{ value: rowNotCoveredText.value?.toString() }} />;
              const rowEntryIDs = thisRow.planEntries.map((entry) => getCleanID(entry.id));
              for (let x = 0; x < thisPlan.planDetailEntries.targetItems.length; x++) {
                if (rowEntryIDs.includes(thisPlan.planDetailEntries.targetItems[x].id)) {
                  showRow = true;
                  cellField = (
                    <RichText
                      field={{ value: thisPlan.planDetailEntries.targetItems[x].description.value?.toString() }}
                    />
                  );
                  break;
                }
              }

              cells.push({ children: cellField });
            });

            if (showRow) {
              newTable.rowData?.push({ cellData: cells });
              addTable = true;
            }
          });

          if (addTable) tempTables.push(newTable);
        });

        if (showRxDrugCoverageTable) {
          drugCoverageGroups.forEach((coverageGroup) => {
            const allOptionIDs = coverageGroup.options.map((drugTable) => getCleanID(drugTable.id));
            const planSelections = plans.map((pln) => pln.prescriptionDrugTables.targetId);

            if (allOptionIDs.filter((id) => planSelections.includes(id)).length > 0) {
              const prescriptionTable: TableProps = {
                id: coverageGroup.id,
                rowData: [],
                className: cx('plan-comparison-table'),
              };

              prescriptionTable.rowData?.push({
                placement: 'header',
                cellData: tempTables.length === 0 ? topRow : topRowNoLink,
              });

              prescriptionTable.rowData?.push({
                placement: 'header',
                cellData: [
                  {
                    children: <RichText field={{ value: coverageGroup.planComparisonTableHeader }} />,
                    colSpan: plans.length + 1,
                    className: cx('header'),
                  },
                ],
              });

              const cells: TableCellData[] = Array<TableCellData>();
              cells.push({ children: <></>, isHeader: true });

              for (let x = 0; x < planSelections.length; x++) {
                const selectedOption = coverageGroup.options.find(
                  (drugTable) => getCleanID(drugTable.id) == planSelections[x]
                );

                if (selectedOption != undefined) {
                  const modalTable: TableProps = {
                    id: 'modalTbl' + getCleanID(plans[x].id) + selectedOption.id,
                    rowData: [
                      {
                        placement: 'header',
                        cellData: [
                          {
                            children: <RichText field={{ value: coverageGroup.groupCaption }} />,
                            colSpan: plans.length + 1,
                            className: cx('header'),
                          },
                        ],
                      },
                    ],
                    className: cx('plan-comparison-table'),
                  };

                  selectedOption.drugCoverageTables.map((thisRxTable) => {
                    if (thisRxTable.rows.length > 0) {
                      modalTable.rowData?.push({
                        cellData: [
                          {
                            children: <></>,
                            colSpan: thisRxTable.columnGroup.columns.length,
                            className: cx('placeholder'),
                          },
                        ],
                      });
                      modalTable.rowData?.push({
                        cellData: [
                          {
                            children: <RichText field={{ value: thisRxTable.caption }} />,
                            isHeader: true,
                            colSpan: thisRxTable.columnGroup.columns.length,
                            className: cx('rx-table-header'),
                          },
                        ],
                      });
                    }

                    thisRxTable.rows.map((newRow) => {
                      const newRowData: TableRowData = { cellData: [] };
                      let firstCell = true;
                      for (let x = 0; x < thisRxTable.columnGroup.columns.length; x++) {
                        //newRow.cells.map((newCell) => {
                        const newCell = newRow.cells[x];
                        const bgColor = thisRxTable.columnGroup.columns[x].color;
                        let labelField = (
                          <RichText
                            field={{
                              value: newCell.label === '' ? rxRowNotCoveredText.value?.toString() : newCell.label,
                            }}
                          />
                        );

                        if (newRow.tooltip !== '' && firstCell) {
                          labelField = (
                            <div className={cx('tooltip-wrapper')}>
                              <RichText
                                field={{
                                  value: newCell.label === '' ? rxRowNotCoveredText.value?.toString() : newCell.label,
                                }}
                              />
                              <Tooltip
                                field={{ value: newRow.tooltip }}
                                className={cx('compare-tooltip')}
                                direction="custom"
                              />
                            </div>
                          );
                        }

                        let tempClass = cx('rx-table-cell');
                        if (newCell.isHeader) {
                          tempClass += ' ' + cx('bold-text');
                        }
                        if (bgColor !== '') {
                          tempClass += x === 0 ? ' bg-gray15' : ' bg-' + bgColor;
                        }

                        newRowData.cellData.push({
                          children: labelField,
                          isHeader: firstCell,
                          className: cx(tempClass),
                        });
                        firstCell = false;
                      }
                      modalTable.rowData?.push(newRowData);
                    });
                  });

                  const cellField = (
                    <Modal
                      title={plans[x].planName.value + ' Table'}
                      launcherText={'View ' + selectedOption.caption + ' Table'}
                      className={cx('rxModalWindow')}
                      width="100%"
                    >
                      <Table {...modalTable}></Table>
                    </Modal>
                  );

                  cells.push({ children: cellField, className: cx('col' + plans.length) });
                } else {
                  const cellField = (
                    <RichText field={{ value: rxTableNotCoveredText.value?.toString() }} editable={false} />
                  );

                  cells.push({ children: cellField, className: cx('col' + plans.length) });
                }
              }
              prescriptionTable.rowData?.push({ cellData: cells });

              tempTables.push(prescriptionTable);
            }
          });
        }

        if (enableEnroll) {
          //table with Apply Here links is always last
          const applyTable: TableProps = {
            id: 'applyTble',
            rowData: [{ placement: 'header', cellData: applyRow }],
            className: cx('plan-comparison-table'),
          };

          tempTables.push(applyTable);
        }

        setTables(tempTables);
        setStage(LoadingState.end);
      }
    }
  }, [
    applyRow,
    baseUrl,
    county,
    drugCoverageGroups,
    enableEnroll,
    existingMember,
    planDetailGroupLayout,
    planDetailPage,
    planSearchPage,
    plans,
    planType,
    planYears,
    rowNotCoveredText,
    router,
    rxRowNotCoveredText,
    rxTableNotCoveredText,
    showNoResultsFound,
    showRxDrugCoverageTable,
    sitecoreContext,
    stage,
    topRow,
    topRowNoLink,
    year,
    zipcode,
    viewPlanDetailsLabel,
    viewApplyToEnrollLabel,
  ]);

  if (stage != LoadingState.end) {
    return (
      <div role="status">
        <span className={cx('screen-reader-only')}>Plan details loaded</span>
        <Loader variant={LoaderVariant.Spinner} />
        <br />
        <br />
      </div>
    );
  }

  if (showNoResultsFound) {
    return (
      <div role="status">
        <strong className={cx('no-plan')}>Error loading plans. Please try again at a later time.</strong>
        <br />
        <br />
      </div>
    );
  }

  return (
    <div className={cx('plan-comparison-page-wrapper')}>
      {tables.map((thistable) => {
        return (
          <div key={thistable.id} className={cx('plan-comparison-table-wrapper')}>
            <Table {...thistable}></Table>
          </div>
        );
      })}
    </div>
  );
};

export default withStyles()<PlanComparisonTableProps>(PlanComparisonTable);
